import React from 'react'
import {Box, BoxProps, HStack, useMultiStyleConfig, VStack, Text, Link} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import {Button, Icon} from '_uiComponents'
import {useViewport} from '_customHooks'
import {breakTmLigature} from '_utils'

interface BoutiqueItemProps {
    name: string
    address: string
    opening: Array<string>
    phone: string
    availability: number
    availabilityMsg: string
    bookAppointmentLink: string
    onCall: () => void
    onBookAppointment: () => void
}

/**
 * @name BoutiqueItem
 *
 * @component
 *
 * @description
 * Renders a boutique list item.
 *
 * @returns {React.FC} - The rendered boutique list item component.
 */
const BoutiqueItem: React.FC<BoutiqueItemProps> = ({
    name,
    address,
    opening,
    phone,
    availability,
    availabilityMsg,
    bookAppointmentLink,
    onCall,
    onBookAppointment,
}: BoutiqueItemProps) => {
    const {isMobile} = useViewport()
    const styles = useMultiStyleConfig('ProductFindInBoutique', {}) as Record<string, BoxProps>

    return (
        <Box w={'100%'} {...styles.boutiqueItemContainer}>
            <VStack {...styles.boutiqueItemWrapper} data-testid="product-find-in-boutique-boutique-item">
                <HStack {...styles.boutiqueItemInfoContainer}>
                    <VStack {...styles.boutiqueItemInfo}>
                        <Text {...styles.boutiqueItemName}>{name}</Text>
                        <Text {...styles.boutiqueItemAddress}>{address}</Text>
                        {/* Render as a div: this wraps other <Text> (<p>) elements, and a <p>
                            cannot legally contain another <p>. */}
                        <Text as="div" {...styles.boutiqueItemOpening}>
                            {isMobile ? (
                                <VStack {...styles.boutiqueItemOpeningMobile}>
                                    {opening.map((o) => (
                                        <Text key={o}>{o}</Text>
                                    ))}
                                </VStack>
                            ) : (
                                <Text>{opening.join(' ')}</Text>
                            )}
                        </Text>
                    </VStack>
                    <Box {...styles.boutiqueItemAvailability}>
                        {isMobile ? (
                            <>
                                {availability === 0 ? (
                                    <Icon name="IconCloseCirle" sx={styles.boutiqueItemUnavailable} />
                                ) : (
                                    <Icon name="IconCheckCircle" sx={availability === 1 ? styles.boutiqueItemAvailableLow : styles.boutiqueItemAvailable} />
                                )}
                            </>
                        ) : (
                            <Text
                                sx={
                                    availability === 0
                                        ? styles.boutiqueItemUnavailable
                                        : availability === 1
                                          ? styles.boutiqueItemAvailableLow
                                          : styles.boutiqueItemAvailable
                                }
                            >
                                {availabilityMsg}
                            </Text>
                        )}
                    </Box>
                </HStack>
                <HStack {...styles.boutiqueItemButtonsContainer}>
                    <Link href={`tel:${phone}`} sx={styles.boutiqueItemLinks}>
                        <Button as={VStack} sx={styles.boutiqueItemCallButton} variant="secondary" onClick={onCall}>
                            <Text {...styles.boutiqueItemButtonsText}>
                                <FormattedMessage id="label.phone.find.in.boutique" defaultMessage={'Call'} />
                            </Text>
                            {!isMobile && <Text {...styles.boutiqueItemButtonsText}>{phone}</Text>}
                        </Button>
                    </Link>
                    <Link href={bookAppointmentLink} sx={styles.boutiqueItemLinks} onClick={onBookAppointment}>
                        <Button as={VStack} sx={styles.boutiqueItemBookButton} variant="primary">
                            <Text {...styles.boutiqueItemBookButtonText}>
                                {/* Break the Safari "TM" -> "™" font ligature (in "appoin*tm*ent") by placing
                                    the two letters in separate DOM elements. See breakTmLigature. */}
                                <FormattedMessage id="label.cta.bookAppointment" defaultMessage={'Book an appointment'}>
                                    {(chunks) => <>{breakTmLigature(chunks.join(''), 'book-appointment')}</>}
                                </FormattedMessage>
                            </Text>
                        </Button>
                    </Link>
                </HStack>
            </VStack>
        </Box>
    )
}

export default BoutiqueItem
